import { useCallback, useEffect, useState } from 'react';
import { InfoCard, Link } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Chip, CircularProgress, Grid, IconButton, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import type { Entity } from '@backstage/catalog-model';
import { kyndrylColors } from '../../theme/kyndrylTheme';

type AgentStatus = 'Running' | 'Pending' | 'Failed' | 'Unknown';

interface AgentInfo {
  entity: Entity;
  status: AgentStatus;
}

const statusChipStyle = (status: AgentStatus): React.CSSProperties => {
  switch (status) {
    case 'Running': return { backgroundColor: kyndrylColors.digitalDarkSpringGreen, color: '#fff' };
    case 'Pending': return { backgroundColor: kyndrylColors.earth, color: '#fff' };
    case 'Failed':  return { backgroundColor: kyndrylColors.warmRed, color: '#fff' };
    default:        return {};
  }
};

const useStyles = makeStyles(theme => ({
  loading: { display: 'flex', justifyContent: 'center', padding: theme.spacing(3) },
  empty:   { padding: theme.spacing(2), color: theme.palette.text.secondary, textAlign: 'center' },
  agentRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
  },
  agentName: { fontSize: '0.85rem' },
}));

export const ActiveAgentsCard = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const { items } = await catalogApi.getEntities({
        filter: { 'metadata.tags': 'agent' },
        fields: [
          'kind', 'metadata.name', 'metadata.namespace',
          'metadata.annotations', 'metadata.title',
        ],
      });

      const k8sBase = await discoveryApi.getBaseUrl('kubernetes');

      const results = await Promise.all(
        items.map(async (entity: Entity): Promise<AgentInfo> => {
          const ns = entity.metadata.annotations?.['backstage.io/kubernetes-namespace'];
          const labelSelector = entity.metadata.annotations?.['backstage.io/kubernetes-label-selector'];

          if (!ns || !labelSelector) return { entity, status: 'Unknown' };

          try {
            const resp = await fetchApi.fetch(
              `${k8sBase}/api/v1/namespaces/${encodeURIComponent(ns)}/pods?labelSelector=${encodeURIComponent(labelSelector)}`,
            );
            if (!resp.ok) return { entity, status: 'Unknown' };
            const data = await resp.json();
            const pods: { status?: { phase?: string } }[] = data.items ?? [];
            if (pods.length === 0) return { entity, status: 'Unknown' };
            const running = pods.some(p => (p.status?.phase ?? '').toLowerCase() === 'running');
            const pending = pods.some(p => (p.status?.phase ?? '').toLowerCase() === 'pending');
            const failed  = pods.every(p => (p.status?.phase ?? '').toLowerCase() === 'failed');
            if (running) return { entity, status: 'Running' };
            if (pending) return { entity, status: 'Pending' };
            if (failed)  return { entity, status: 'Failed' };
          } catch {
            // silent — agent namespace may not be reachable in dev
          }
          return { entity, status: 'Unknown' };
        }),
      );

      setAgents(results);
    } catch {
      // non-fatal
    } finally {
      setLoading(false);
    }
  }, [catalogApi, discoveryApi, fetchApi]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const action = (
    <Tooltip title="Refresh">
      <span>
        <IconButton size="small" onClick={fetchAgents} disabled={loading}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <InfoCard title="Active Agents" subheader="In-cluster agent status" action={action}>
      {loading ? (
        <div className={classes.loading}><CircularProgress size={24} /></div>
      ) : null}
      {!loading && agents.length === 0 ? (
        <Typography className={classes.empty}>No agent entities found in the catalog.</Typography>
      ) : null}
      {!loading && agents.length > 0 ? (
        <Grid container direction="column">
          {agents.map(({ entity, status }) => {
            const ns = entity.metadata.namespace ?? 'default';
            const href = `/catalog/${ns}/component/${entity.metadata.name}`;
            const displayName = entity.metadata.annotations?.['backstage.io/display-name']
              ?? (entity.metadata as any).title
              ?? entity.metadata.name;
            return (
              <Grid item key={entity.metadata.name} className={classes.agentRow}>
                <Link to={href}>
                  <Typography className={classes.agentName}>{displayName}</Typography>
                </Link>
                <Chip
                  label={status}
                  size="small"
                  style={statusChipStyle(status)}
                />
              </Grid>
            );
          })}
        </Grid>
      ) : null}
    </InfoCard>
  );
};
